<template>
  <MainLayout
    :initialDrawerExpanded="false"
    contentClass="pa-md-4"
    :has-sidebar="false"
  >
    <template #header>
      <div class="overline">
        <PageBack />
      </div>
    </template>

    <template #content>
      <v-container fluid class="pa-0 pa-md-4">
        <v-row>
          <v-col cols="12" md="10" offset-md="1">

            <div class="d-flex align-center mb-4">
              <h2>
                <v-icon class="mr-2">mdi-shield-crown-outline</v-icon>
                Administration
              </h2>
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    class="ml-4"
                    icon
                    :loading="loading"
                    aria-label="Actualiser"
                    v-bind="attrs"
                    v-on="on"
                    @click="loadAll"
                  >
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>Actualiser</span>
              </v-tooltip>
            </div>

            <v-alert
              v-if="celeryStatus === 'down'"
              type="error"
              dense
              text
              class="mb-4"
            >
              <v-icon left small>mdi-robot-off-outline</v-icon>
              Worker Celery injoignable — les tâches en attente ne seront pas traitées.
            </v-alert>

            <!-- Stats cards -->
            <v-row class="mb-4">
              <v-col cols="12" sm="3">
                <v-card elevation="0">
                  <v-card-text class="d-flex justify-center align-center">
                    <v-icon large color="primary" class="mr-4">mdi-account-group-outline</v-icon>
                    <div>
                      <div class="display-1 font-weight-bold">{{ stats.users ?? '—' }}</div>
                      <div class="caption grey--text">Utilisateurs</div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="3">
                <v-card elevation="0">
                  <v-card-text class="d-flex justify-center align-center">
                    <v-icon large color="primary" class="mr-4">mdi-chat-outline</v-icon>
                    <div>
                      <div class="display-1 font-weight-bold">{{ stats.conversations ?? '—' }}</div>
                      <div class="caption grey--text">Conversations</div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="3">
                <v-card elevation="0">
                  <v-card-text class="d-flex justify-center align-center">
                    <v-icon large color="primary" class="mr-4">mdi-folder-outline</v-icon>
                    <div>
                      <div class="display-1 font-weight-bold">{{ stats.projects ?? '—' }}</div>
                      <div class="caption grey--text">Projets</div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="3">
                <v-card elevation="0">
                  <v-card-text class="d-flex justify-center align-center">
                    <v-icon large color="primary" class="mr-4">mdi-robot-outline</v-icon>
                    <div>
                      <div class="display-1 font-weight-bold">{{ stats.engines ?? '—' }}</div>
                      <div class="caption grey--text">Moteurs</div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Tabs -->
            <v-card elevation="0">
              <v-tabs v-model="tab" background-color="transparent" :color="tabColor" :slider-color="tabColor">
                <v-tab>
                  <v-icon left small :color="tabColors[0]">mdi-account-multiple-outline</v-icon>
                  Utilisateurs
                </v-tab>
                <v-tab>
                  <v-icon left small :color="tabColors[1]">mdi-domain</v-icon>
                  Organisations
                </v-tab>
                <v-tab>
                  <v-icon left small :color="tabColors[2]">mdi-robot-outline</v-icon>
                  Moteurs
                </v-tab>
                <v-tab>
                  <v-icon left small :color="tabColors[3]">mdi-state-machine</v-icon>
                  Sessions Agent
                </v-tab>
                <v-tab>
                  <v-icon left small :color="tabColors[4]">mdi-currency-usd</v-icon>
                  Tarifs
                </v-tab>
                <v-tab>
                  <v-icon left small :color="tabColors[5]">mdi-file-multiple-outline</v-icon>
                  Fichiers
                </v-tab>
                <v-tab>
                  <v-icon left small :color="tabColors[6]">mdi-cog-sync-outline</v-icon>
                  Tâches
                </v-tab>
                <v-tab>
                  <v-icon left small :color="tabColors[7]">mdi-warehouse</v-icon>
                  Plans de stock
                </v-tab>
              </v-tabs>

              <v-divider />

              <v-tabs-items v-model="tab">

                <!-- Utilisateurs -->
                <v-tab-item>
                  <v-data-table
                    :headers="userHeaders"
                    :items="users"
                    :loading="loading"
                    sort-by="username"
                    class="elevation-0"
                  >
                    <template v-slot:[`item.username`]="{ item }">
                      <a class="font-weight-medium text-decoration-none" style="cursor:pointer"
                         @click="$router.push({ name: 'admin_user_detail', params: { id: item.id } })">
                        {{ item.username }}
                      </a>
                    </template>
                    <template v-slot:[`item.organisation_name`]="{ item }">
                      <div v-if="item.organisation_name" class="d-flex align-center">
                        <v-avatar size="20" class="mr-2">
                          <v-img v-if="item.organisation_icon" :src="item.organisation_icon" />
                          <v-icon v-else small>mdi-domain</v-icon>
                        </v-avatar>
                        <span class="caption">{{ item.organisation_name }}</span>
                      </div>
                      <span v-else class="grey--text">—</span>
                    </template>
                    <template v-slot:[`item.is_staff`]="{ item }">
                      <v-icon small :color="item.is_staff ? 'primary' : 'grey lighten-2'">
                        {{ item.is_staff ? 'mdi-check-circle' : 'mdi-minus-circle-outline' }}
                      </v-icon>
                    </template>
                    <template v-slot:[`item.is_demo`]="{ item }">
                      <v-icon small :color="item.is_demo ? 'orange' : 'grey lighten-2'">
                        {{ item.is_demo ? 'mdi-check-circle' : 'mdi-minus-circle-outline' }}
                      </v-icon>
                    </template>
                    <template v-slot:[`item.is_active`]="{ item }">
                      <v-icon small :color="item.is_active ? 'success' : 'error'">
                        {{ item.is_active ? 'mdi-check-circle' : 'mdi-minus-circle-outline' }}
                      </v-icon>
                    </template>
                    <template v-slot:[`item.last_login`]="{ item }">
                      <span class="caption">{{ formatDate(item.last_login) }}</span>
                    </template>
                  </v-data-table>
                </v-tab-item>

                <!-- Organisations -->
                <v-tab-item>
                  <v-data-table
                    :headers="organisationHeaders"
                    :items="organisations"
                    :loading="loading"
                    sort-by="name"
                    class="elevation-0"
                  >
                    <template v-slot:[`item.name`]="{ item }">
                      <a
                        class="font-weight-medium text-decoration-none"
                        style="cursor:pointer"
                        @click="$router.push({ name: 'admin_organisation_detail', params: { id: item.id } })"
                      >{{ item.name }}</a>
                    </template>
                    <template v-slot:[`item.icon`]="{ item }">
                      <v-avatar size="20">
                        <v-img v-if="item.icon" :src="item.icon" />
                        <v-icon v-else small>mdi-domain</v-icon>
                      </v-avatar>
                    </template>
                    <template v-slot:[`item.created_at`]="{ item }">{{ formatDate(item.created_at) }}</template>
                  </v-data-table>
                </v-tab-item>

                <!-- Moteurs -->
                <v-tab-item>
                  <v-data-table
                    :headers="engineHeaders"
                    :items="engines"
                    :loading="loading"
                    sort-by="name"
                    class="elevation-0"
                  >
                    <template v-slot:[`item.name`]="{ item }">
                      <a
                        class="font-weight-medium text-decoration-none"
                        style="cursor:pointer"
                        @click="$router.push({ name: 'admin_engine_edit', params: { id: item.id } })"
                      >{{ item.name }}</a>
                    </template>
                    <template v-slot:[`item.provider_slug`]="{ item }">
                      <div class="d-flex align-center">
                        <open-ai-logo v-if="item.provider_slug === 'openai'" class="mr-1" />
                        <span class="caption">{{ formatProvider(item.provider_slug) }}</span>
                      </div>
                    </template>
                    <template v-slot:[`item.is_active`]="{ item }">
                      <v-icon small :color="item.is_active ? 'success' : 'grey lighten-2'">
                        {{ item.is_active ? 'mdi-check-circle' : 'mdi-minus-circle-outline' }}
                      </v-icon>
                    </template>
                    <template v-slot:[`item.is_demo`]="{ item }">
                      <v-icon small :color="item.is_demo ? 'orange' : 'grey lighten-2'">
                        {{ item.is_demo ? 'mdi-check-circle' : 'mdi-minus-circle-outline' }}
                      </v-icon>
                    </template>
                    <template v-slot:[`item.user`]="{ item }">
                      <span class="caption">{{ item.user || '—' }}</span>
                    </template>
                    <template v-slot:[`item.active_prompt`]="{ item }">
                      <span v-if="item.active_prompt" class="caption">
                        {{ item.active_prompt.name }}
                        <span class="grey--text text--lighten-1">v{{ item.active_prompt.version }}</span>
                      </span>
                      <span v-else class="caption grey--text">—</span>
                    </template>
                    <template v-slot:[`item.folder_mapping`]="{ item }">
                      <template v-if="item.folder_mapping">
                        <v-btn
                          text x-small
                          :href="item.folder_mapping.drive_folder_url"
                          target="_blank"
                          rel="noopener"
                        >
                          <v-icon left x-small>mdi-google-drive</v-icon>
                          Drive
                          <v-icon right x-small>mdi-open-in-new</v-icon>
                        </v-btn>
                        <v-btn
                          text x-small
                          :href="item.folder_mapping.vector_store_url"
                          target="_blank"
                          rel="noopener"
                        >
                          <open-ai-logo :size="12" class="mr-1" />
                          OpenAI
                          <v-icon right x-small>mdi-open-in-new</v-icon>
                        </v-btn>
                      </template>
                      <span v-else class="caption grey--text">—</span>
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                      <v-btn icon small @click="$router.push({ name: 'admin_engine_edit', params: { id: item.id } })">
                        <v-icon small>mdi-pencil-outline</v-icon>
                      </v-btn>
                      <v-btn v-if="isSuperuser" icon small color="error" @click="confirmDeleteEngine(item)">
                        <v-icon small>mdi-delete-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-tab-item>

                <!-- Sessions Agent -->
                <v-tab-item>
                  <v-data-table
                    :headers="agentSessionHeaders"
                    :items="agentSessions"
                    :loading="loading"
                    sort-by="created_at"
                    :sort-desc="true"
                    class="elevation-0"
                    @click:row="item => $router.push({ name: 'admin_agent_session_detail', params: { id: item.id } })"
                    style="cursor: pointer"
                  >
                    <template v-slot:[`item.initial_message`]="{ item }">
                      <a
                        class="text-decoration-none"
                        style="cursor:pointer"
                        @click.stop="$router.push({ name: 'admin_agent_session_detail', params: { id: item.id } })"
                      >
                        <span class="caption" style="max-width:280px;display:inline-block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
                          {{ item.initial_message }}
                        </span>
                      </a>
                    </template>
                    <template v-slot:[`item.outcome_type`]="{ item }">
                      <v-chip x-small :color="item.outcome_type === 'project' ? 'primary' : 'teal'" dark v-if="item.outcome_type">
                        {{ item.outcome_type === 'project' ? 'Projet' : 'Conversation' }}
                      </v-chip>
                      <span v-else class="grey--text caption">—</span>
                    </template>
                    <template v-slot:[`item.result`]="{ item }">
                      <span v-if="item.project_id" class="caption">#{{ item.project_id }}</span>
                      <span v-else-if="item.conversation_id" class="caption">#{{ item.conversation_id }}</span>
                      <span v-else class="grey--text caption">—</span>
                    </template>
                    <template v-slot:[`item.created_at`]="{ item }">
                      {{ formatDate(item.created_at) }}
                    </template>
                  </v-data-table>
                </v-tab-item>

                <!-- Tarifs -->
                <v-tab-item>
                  <v-data-table
                    :headers="pricingHeaders"
                    :items="pricing"
                    :loading="loading"
                    sort-by="provider_slug"
                    class="elevation-0"
                  >
                    <template v-slot:[`item.input_per_1m`]="{ item }">
                      {{ formatPrice(item.input_per_1m) }}
                    </template>
                    <template v-slot:[`item.output_per_1m`]="{ item }">
                      {{ formatPrice(item.output_per_1m) }}
                    </template>
                    <template v-slot:[`item.effective_from`]="{ item }">
                      {{ formatDate(item.effective_from) }}
                    </template>
                    <template v-slot:[`item.effective_to`]="{ item }">
                      <span v-if="item.effective_to">{{ formatDate(item.effective_to) }}</span>
                      <v-chip small color="success" v-else>Actuel</v-chip>
                    </template>
                  </v-data-table>
                </v-tab-item>


                <!-- Fichiers -->
                <v-tab-item>
                  <v-data-table
                    :headers="fileHeaders"
                    :items="files"
                    :loading="loading"
                    sort-by="uploaded_at"
                    :sort-desc="true"
                    class="elevation-0"
                  >
                    <template v-slot:[`item.name`]="{ item }">
                      <a
                        v-if="isSpreadsheetExt(item.file_ext)"
                        class="font-weight-medium text-decoration-none"
                        style="cursor:pointer"
                        @click="$router.push({ name: 'admin_file_spreadsheet', params: { id: item.id } })"
                      >{{ item.name }}</a>
                      <span v-else>{{ item.name }}</span>
                    </template>
                    <template v-slot:[`item.file_type`]="{ item }">
                      <v-chip v-if="item.file_type" x-small :color="isSpreadsheetExt(item.file_ext) ? 'teal' : ''" :dark="isSpreadsheetExt(item.file_ext)">
                        {{ item.file_type }}
                      </v-chip>
                      <span v-else class="grey--text">—</span>
                    </template>
                    <template v-slot:[`item.organisation`]="{ item }">
                      <span class="caption">{{ item.organisation || '—' }}</span>
                    </template>
                    <template v-slot:[`item.team`]="{ item }">
                      <span class="caption">{{ item.team || '—' }}</span>
                    </template>
                    <template v-slot:[`item.owner`]="{ item }">
                      <span class="caption">{{ item.owner || '—' }}</span>
                    </template>
                    <template v-slot:[`item.uploaded_at`]="{ item }">
                      <span class="caption">{{ formatDate(item.uploaded_at) }}</span>
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                      <v-btn
                        v-if="isSpreadsheetExt(item.file_ext)"
                        icon small
                        @click="$router.push({ name: 'admin_file_spreadsheet', params: { id: item.id } })"
                      >
                        <v-icon small>mdi-table-eye</v-icon>
                      </v-btn>
                      <v-btn icon small :href="item.file_url" target="_blank" rel="noopener" :disabled="!item.file_url">
                        <v-icon small>mdi-download-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-tab-item>

                <!-- Tâches -->
                <v-tab-item>
                  <v-data-table
                    :headers="taskHeaders"
                    :items="tasks"
                    :loading="loading"
                    sort-by="date_created"
                    :sort-desc="true"
                    class="elevation-0"
                  >
                    <template v-slot:[`item.status`]="{ item }">
                      <v-chip x-small :color="taskStatusColor(item.status)" dark>
                        {{ item.status }}
                      </v-chip>
                    </template>
                    <template v-slot:[`item.date_created`]="{ item }">
                      <span class="caption">{{ formatDate(item.date_created) }}</span>
                    </template>
                    <template v-slot:[`item.date_done`]="{ item }">
                      <span class="caption">{{ formatDate(item.date_done) }}</span>
                    </template>
                    <template v-slot:[`item.worker`]="{ item }">
                      <span class="caption">{{ item.worker || '—' }}</span>
                    </template>
                    <template v-slot:[`item.duration`]="{ item }">
                      <span class="caption">{{ taskDuration(item) }}</span>
                    </template>
                    <template v-slot:[`item.details`]="{ item }">
                      <v-btn icon small @click="openTaskDetails(item)">
                        <v-icon small>mdi-information-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-tab-item>

                <!-- Plans de stock -->
                <v-tab-item>
                  <AdminStockPlansPanel />
                </v-tab-item>

              </v-tabs-items>
            </v-card>

          </v-col>
        </v-row>

        <!-- Tools row -->
        <v-row class="mt-2">
          <v-col cols="12" md="10" offset-md="1">
            <v-card elevation="0">
              <v-card-title class="subtitle-1 font-weight-medium pb-1">
                <v-icon left small>mdi-tools</v-icon>
                Outils
              </v-card-title>
              <v-card-text class="pt-0">
                <v-btn
                  text small
                  @click="$router.push({ name: 'admin_comparative' })"
                >
                  <v-icon left small>mdi-compare</v-icon>
                  <span class="text-none">Test comparatif</span>
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

      </v-container>

      <!-- Engine create/edit dialog -->
      <v-dialog v-model="engineDialog" max-width="520" @keydown.esc="engineDialog = false">
        <v-card>
          <v-card-title class="subtitle-1 font-weight-medium">
            {{ editingEngine ? 'Modifier le moteur' : 'Nouveau moteur' }}
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4">
            <v-text-field
              v-model="engineForm.name"
              label="Nom"
              outlined dense
              :error-messages="engineFormErrors.name"
            />
            <v-select
              v-model="engineForm.provider_slug"
              :items="providerChoices"
              label="Fournisseur"
              outlined dense
              :error-messages="engineFormErrors.provider_slug"
            />
            <v-text-field
              v-model="engineForm.model"
              label="Modèle"
              outlined dense
              :error-messages="engineFormErrors.model"
            />
            <v-select
              v-model="engineForm.active_prompt_id"
              :items="promptChoices"
              label="Prompt système"
              outlined dense
              clearable
              :error-messages="engineFormErrors.active_prompt_id"
            />
            <div class="d-flex">
              <v-checkbox v-model="engineForm.is_active" label="Actif" class="mr-4" />
              <v-checkbox v-model="engineForm.is_demo" label="Démo" />
            </div>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="engineDialog = false">Annuler</v-btn>
            <v-btn color="primary" elevation="0" :loading="engineSaving" @click="saveEngine">
              {{ editingEngine ? 'Enregistrer' : 'Créer' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Engine delete confirmation dialog -->
      <v-dialog v-model="engineDeleteDialog" max-width="400">
        <v-card>
          <v-card-title class="subtitle-1 font-weight-medium">Supprimer le moteur</v-card-title>
          <v-card-text>
            Supprimer <strong>{{ deletingEngine && deletingEngine.name }}</strong> ? Cette action est irréversible.
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="engineDeleteDialog = false">Annuler</v-btn>
            <v-btn color="error" elevation="0" :loading="engineDeleting" @click="deleteEngine">Supprimer</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Task details dialog -->
      <v-dialog v-model="taskDetailDialog" max-width="640" @keydown.esc="taskDetailDialog = false">
        <v-card v-if="selectedTask">
          <v-card-title class="subtitle-1 font-weight-medium">
            {{ selectedTask.task_name }}
            <v-chip x-small :color="taskStatusColor(selectedTask.status)" dark class="ml-2">
              {{ selectedTask.status }}
            </v-chip>
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4">
            <div class="caption mb-3">
              <div>Worker : {{ selectedTask.worker || '—' }}</div>
              <div>Créée : {{ formatDate(selectedTask.date_created) }}</div>
              <div>Démarrée : {{ formatDate(selectedTask.date_started) }}</div>
              <div>Terminée : {{ formatDate(selectedTask.date_done) }}</div>
              <div>Durée : {{ taskDuration(selectedTask) }}</div>
            </div>

            <template v-if="formatJson(selectedTask.task_args)">
              <div class="overline">Args</div>
              <pre class="caption grey lighten-4 pa-2 rounded task-pre">{{ formatJson(selectedTask.task_args) }}</pre>
            </template>

            <template v-if="formatJson(selectedTask.task_kwargs)">
              <div class="overline mt-2">Kwargs</div>
              <pre class="caption grey lighten-4 pa-2 rounded task-pre">{{ formatJson(selectedTask.task_kwargs) }}</pre>
            </template>

            <template v-if="formatJson(selectedTask.result)">
              <div class="overline mt-2">Résultat</div>
              <pre class="caption grey lighten-4 pa-2 rounded task-pre">{{ formatJson(selectedTask.result) }}</pre>
            </template>

            <template v-if="selectedTask.traceback">
              <div class="overline mt-2 error--text">Erreur</div>
              <pre class="caption red lighten-5 error--text pa-2 rounded task-pre">{{ selectedTask.traceback }}</pre>
            </template>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="taskDetailDialog = false">Fermer</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </MainLayout>
</template>

<script>
import PageBack from '@/components/ui/PageBack.vue'
import MainLayout from '@/layout/MainLayout.vue'
import OpenAiLogo from '@/components/ui/OpenAiLogo.vue'
import AdminStockPlansPanel from '@/components/admin/AdminStockPlansPanel.vue'
import {
  fetchAdminStats,
  fetchAdminUsers,
  fetchAdminOrganisations,
  fetchAdminConversations,
  fetchAdminEngines,
  fetchAdminPricing,
  fetchAdminAgentSessions,
  fetchAdminFiles,
  fetchAdminTasks,
  createAdminEngine,
  updateAdminEngine,
  deleteAdminEngine,
  fetchAdminPrompts,
} from '@/services/adminApi'
import { fetchHealth } from '@/services/healthApi'

const PROVIDER_LABELS = { openai: 'OpenAI' }

const TAB_SLUGS = ['utilisateurs', 'organisations', 'moteurs', 'sessions', 'tarifs', 'fichiers', 'taches', 'plans-stock']

// Same category colours as the Staff menu (Comptes / LLM & Agentic / Setups / Outils), aligned with TAB_SLUGS.
const TAB_COLORS = ['blue', 'blue', 'deep-purple', 'deep-purple', 'deep-purple', 'orange darken-1', 'teal', 'orange darken-1']

const HEALTH_POLL_INTERVAL_MS = 30000

const TASK_STATUS_COLORS = {
  SUCCESS: 'success',
  FAILURE: 'error',
  PROGRESS: 'primary',
  STARTED: 'primary',
  RETRY: 'orange',
  PENDING: 'grey',
}

export default {
  name: 'AdminView',
  components: { PageBack, MainLayout, OpenAiLogo, AdminStockPlansPanel },

  data() {
    return {
      tabColors: TAB_COLORS,
      tab: TAB_SLUGS.indexOf(this.$route.query.tab) !== -1
        ? TAB_SLUGS.indexOf(this.$route.query.tab)
        : 0,
      loading: false,

      // Stats
      stats: {},

      // Table data
      users: [],
      organisations: [],
      conversations: [],
      engines: [],
      pricing: [],
      agentSessions: [],
      files: [],
      tasks: [],

      // Engine management
      engineDialog: false,
      editingEngine: null,
      engineForm: { name: '', provider_slug: 'openai', model: '', active_prompt_id: null, is_active: true, is_demo: false },
      engineFormErrors: {},
      engineSaving: false,
      engineDeleteDialog: false,
      deletingEngine: null,
      engineDeleting: false,

      providerChoices: [{ text: 'OpenAI', value: 'openai' }],

      // Table headers
      engineHeaders: [
        { text: 'Nom', value: 'name' },
        { text: 'Fournisseur', value: 'provider_slug' },
        { text: 'Modèle', value: 'model' },
        { text: 'Prompt', value: 'active_prompt', sortable: false },
        { text: 'Vector Store', value: 'folder_mapping', sortable: false },
        { text: 'Actif', value: 'is_active', align: 'center' },
        { text: 'Démo', value: 'is_demo', align: 'center' },
        { text: 'Utilisateur', value: 'user' },
        { text: '', value: 'actions', sortable: false, align: 'right' },
      ],
      userHeaders: [
        { text: 'Utilisateur', value: 'username' },
        { text: 'Prénom', value: 'first_name' },
        { text: 'Nom', value: 'last_name' },
        { text: 'Email', value: 'email' },
        { text: 'Organisation', value: 'organisation_name' },
        { text: 'Actif', value: 'is_active', align: 'center' },
        { text: 'Staff', value: 'is_staff', align: 'center' },
        { text: 'Démo', value: 'is_demo', align: 'center' },
        { text: 'Conversations', value: 'conversation_count', align: 'right' },
        { text: 'Dernière connexion', value: 'last_login' },
      ],
      organisationHeaders: [
        { text: '', value: 'icon', sortable: false, width: 40 },
        { text: 'Nom', value: 'name' },
        { text: 'Équipes', value: 'team_count', align: 'right' },
        { text: 'Membres', value: 'member_count', align: 'right' },
        { text: 'Créée', value: 'created_at' },
      ],
      conversationHeaders: [
        { text: 'Titre', value: 'title' },
        { text: 'Utilisateur', value: 'user' },
        { text: 'Moteur', value: 'engine' },
        { text: 'Messages', value: 'message_count', align: 'right' },
        { text: 'Modifié', value: 'updated_at' },
        { text: 'Créé', value: 'created_at' },
      ],
      pricingHeaders: [
        { text: 'Fournisseur', value: 'provider_slug' },
        { text: 'Modèle', value: 'model' },
        { text: 'Entrée / 1M tokens', value: 'input_per_1m', align: 'right' },
        { text: 'Sortie / 1M tokens', value: 'output_per_1m', align: 'right' },
        { text: 'Depuis', value: 'effective_from' },
        { text: 'Jusqu\'au', value: 'effective_to' },
      ],
      agentSessionHeaders: [
        { text: 'Utilisateur', value: 'user' },
        { text: 'Message initial', value: 'initial_message', sortable: false },
        { text: 'Stage 1', value: 'stage1' },
        { text: 'Stage 2', value: 'stage2' },
        { text: 'Résultat', value: 'outcome_type' },
        { text: 'ID', value: 'result', sortable: false },
        { text: 'Créé', value: 'created_at' },
      ],
      fileHeaders: [
        { text: 'Nom', value: 'name' },
        { text: 'Type', value: 'file_type' },
        { text: 'Organisation', value: 'organisation' },
        { text: 'Équipe', value: 'team' },
        { text: 'Propriétaire', value: 'owner' },
        { text: 'Ajouté le', value: 'uploaded_at' },
        { text: '', value: 'actions', sortable: false, align: 'right' },
      ],
      taskHeaders: [
        { text: 'Tâche', value: 'task_name' },
        { text: 'Statut', value: 'status' },
        { text: 'Worker', value: 'worker' },
        { text: 'Créée', value: 'date_created' },
        { text: 'Terminée', value: 'date_done' },
        { text: 'Durée', value: 'duration', sortable: false },
        { text: '', value: 'details', sortable: false, align: 'right' },
      ],
      taskDetailDialog: false,
      selectedTask: null,
      celeryStatus: null,

      // Prompts (for engine form dropdown)
      prompts: [],
    }
  },

  computed: {
    tabColor() { return this.tabColors[this.tab] },
    isSuperuser() {
      return this.$store.getters['settings/isSuperuser']
    },
    promptChoices() {
      return this.prompts.map(p => ({ text: p.name, value: p.id }))
    },
  },

  watch: {
    tab(idx) {
      const slug = TAB_SLUGS[idx]
      if (this.$route.query.tab !== slug) {
        this.$router.replace({ query: { ...this.$route.query, tab: slug } })
      }
    },
    '$route.query.tab'(slug) {
      const idx = TAB_SLUGS.indexOf(slug)
      if (idx !== -1 && this.tab !== idx) this.tab = idx
    },
  },

  mounted() {
    this.loadAll()
    this.loadPrompts()
    this.pollHealth()
    this.healthPollTimer = setInterval(this.pollHealth, HEALTH_POLL_INTERVAL_MS)
  },

  beforeDestroy() {
    clearInterval(this.healthPollTimer)
  },

  methods: {
    async pollHealth() {
      try {
        const { celery } = await fetchHealth()
        this.celeryStatus = celery
      } catch {
        // Health endpoint itself unreachable — leave the last known
        // celeryStatus as-is rather than flipping the indicator on a
        // transient network blip.
      }
    },

    async loadAll() {
      this.loading = true
      try {
        const [stats, users, organisations, conversations, engines, pricing, agentSessions, files, tasks] = await Promise.all([
          fetchAdminStats(),
          fetchAdminUsers(),
          fetchAdminOrganisations(),
          fetchAdminConversations(),
          fetchAdminEngines(),
          fetchAdminPricing(),
          fetchAdminAgentSessions(),
          fetchAdminFiles(),
          fetchAdminTasks(),
        ])
        this.stats = stats
        this.users = users
        this.organisations = organisations
        this.conversations = conversations
        this.engines = engines
        this.pricing = pricing
        this.agentSessions = agentSessions
        this.files = files
        this.tasks = tasks
      } catch {
        this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: 'Erreur lors du chargement des données admin.' })
      } finally {
        this.loading = false
      }
    },

    isSpreadsheetExt(ext) {
      return ['xls', 'xlsx', 'xlsm', 'csv', 'json'].includes(ext || '')
    },

    formatProvider(slug) {
      return PROVIDER_LABELS[slug] || slug
    },

    formatDate(iso) {
      if (!iso) return '—'
      return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
    },

    formatPrice(val) {
      return `$${parseFloat(val).toFixed(4)}`
    },

    formatJson(value) {
      if (value === null || value === undefined || value === '') return null
      return typeof value === 'string' ? value : JSON.stringify(value, null, 2)
    },

    taskStatusColor(status) {
      return TASK_STATUS_COLORS[status] || 'grey'
    },

    taskDuration(item) {
      if (!item.date_started || !item.date_done) return '—'
      const seconds = (new Date(item.date_done) - new Date(item.date_started)) / 1000
      if (seconds < 60) return `${seconds.toFixed(1)}s`
      const minutes = Math.floor(seconds / 60)
      return `${minutes}m ${Math.round(seconds % 60)}s`
    },

    openTaskDetails(item) {
      this.selectedTask = item
      this.taskDetailDialog = true
    },

    // --- Engine management ---

    openCreateEngine() {
      this.editingEngine = null
      this.engineForm = { name: '', provider_slug: 'openai', model: '', active_prompt_id: null, is_active: true, is_demo: false }
      this.engineFormErrors = {}
      this.engineDialog = true
    },

    async saveEngine() {
      this.engineSaving = true
      this.engineFormErrors = {}
      try {
        if (this.editingEngine) {
          const updated = await updateAdminEngine(this.editingEngine.id, this.engineForm)
          const idx = this.engines.findIndex(e => e.id === this.editingEngine.id)
          if (idx !== -1) this.$set(this.engines, idx, updated)
        } else {
          const created = await createAdminEngine(this.engineForm)
          this.engines.push(created)
        }
        this.engineDialog = false
      } catch (e) {
        if (e.data) {
          this.engineFormErrors = e.data
        } else {
          this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: 'Erreur lors de la sauvegarde.' })
        }
      } finally {
        this.engineSaving = false
      }
    },

    confirmDeleteEngine(engine) {
      this.deletingEngine = engine
      this.engineDeleteDialog = true
    },

    async deleteEngine() {
      this.engineDeleting = true
      try {
        await deleteAdminEngine(this.deletingEngine.id)
        this.engines = this.engines.filter(e => e.id !== this.deletingEngine.id)
        this.engineDeleteDialog = false
      } catch {
        this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: 'Erreur lors de la suppression.' })
      } finally {
        this.engineDeleting = false
      }
    },

    // --- Prompts (for engine form dropdown only) ---

    async loadPrompts() {
      try {
        this.prompts = await fetchAdminPrompts()
      } catch {
        // non-critical, engine form will just have no prompt choices
      }
    },
  },
}
</script>

<style scoped>
.task-pre {
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 240px;
  overflow-y: auto;
}
</style>
